import React, { useEffect, useState, useRef } from "react";
import styles from "./AddNewTaskModal.module.scss";
import classNames from "classnames/bind";
import { notify } from "~/utils/helper";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import dayjs from "dayjs";
import BaseModal from "~/components/common/base-modal/BaseModal";
import userManagementApi from "~/apis/userManagement";
import { IOverviewJobTask } from "~/utils/interface/jobDetails";

const cx = classNames.bind(styles);

interface AddNewTaskModalProps {
  isOpen: boolean;
  initialData: IOverviewJobTask | null;
  jobId: string;
  onClose: () => void;
  onSave: (data: any, mode: "add" | "edit") => Promise<void>;
  mode?: "add" | "edit";
  isLoading?: boolean;
}

const TASK_TYPE_OPTIONS = [
  { label: "Phone call", value: "PHONE_CALL" },
  { label: "Initial appointment", value: "INITIAL_APPOINTMENT" },
  { label: "Adjuster meeting", value: "ADJUSTER_MEETING" },
  { label: "Estimate follow-up", value: "ESTIMATE_FOLLOW_UP" },
  { label: "Invoice follow-up", value: "INVOICE_FOLLOW_UP" },
  { label: "Meeting", value: "MEETING" },
  { label: "Collections follow-ups", value: "COLLECTIONS_FOLLOW_UPS" },
  { label: "Job notes", value: "JOB_NOTES" },
  { label: "Sick day", value: "SICK_DAY" },
  { label: "Job follow-ups", value: "JOB_FOLLOW_UPS" },
  { label: "Signing", value: "SIGNING" },
  { label: "Office", value: "OFFICE" },
  { label: "Reschedule sick", value: "RESCHEDULE_SICK" },
  { label: "Vacay", value: "VACAY" },
  { label: "Reschedule due to weather", value: "RESCHEDULE_WEATHER" },
  { label: "Holiday", value: "HOLIDAY" },
  { label: "Training", value: "TRAINING" },
];

const PRIORITY_OPTIONS = [
  { label: "Urgent", value: "URGENT" },
  { label: "High", value: "HIGH" },
  { label: "Normal", value: "NORMAL" },
  { label: "Low", value: "LOW" },
];


const STATUS_OPTIONS = [
  { label: "In Progress", value: "In Progress" },
  { label: "Completed", value: "Completed" },
];

const AddNewTaskModal: React.FC<AddNewTaskModalProps> = ({
  isOpen,
  initialData,
  jobId,
  onClose,
  onSave,
  mode = "add",
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    taskType: "",
    taskName: "",
    description: "",
    allDayChecked: false,
    startDate: "",
    startTime: "",
    dueDate: "",
    dueTime: "",
    priority: "",
    assignedTo: "",
    status: "",
    subcontractor: "",
  });
  const [employees, setEmployees] = useState<
    { label: string; value: string }[]
  >([]);
  const [subcontractors, setSubcontractors] = useState<
    { label: string; value: string }[]
  >([]);
  const [assignedTo, setAssignedTo] = useState<
    { label: string; value: string }[]
  >([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const bodyRef = useRef<HTMLDivElement>(null);


  // Populate form with initialData when in edit mode
  useEffect(() => {
    setErrors({});
    if (mode === "edit" && initialData) {
      setFormData({
        taskType: initialData.type || "",
        taskName: initialData.title || "",
        description: initialData.description || "",
        allDayChecked: initialData.allDayChecked || false,
        startDate: initialData.startDate
          ? dayjs(initialData.startDate).format("YYYY-MM-DD")
          : "",
        startTime: initialData.startDate ? initialData.startDate : "",
        dueDate: initialData.dueDate
          ? dayjs(initialData.dueDate).format("YYYY-MM-DD")
          : "",
        dueTime: initialData.dueDate ? initialData.dueDate : "",
        priority: initialData.priority || "",
        assignedTo: initialData.assigneeId?.toString() || "",
        status: initialData.status || "",
        subcontractor: initialData.subContractorId?.toString() || "",
      });
    } else if (mode === "add") {
      // Reset form when switching to add mode
      setFormData({
        taskType: "",
        taskName: "",
        description: "",
        allDayChecked: false,
        startDate: "",
        startTime: "",
        dueDate: "",
        dueTime: "",
        priority: "",
        assignedTo: "",
        status: "",
        subcontractor: "",
      });
    }
    // isOpen: re-run on every open, otherwise Add -> Save -> Add keeps the previous entry (mode/initialData unchanged)
  }, [mode, initialData, isOpen]);

  useEffect(() => {
    const fetchEmployees = async () => {
      const response = await userManagementApi.getEmployees({});
      if (response.data?.data?.responses) {
        setEmployees(
          response.data.data.responses.map((employee: any) => ({
            label: `${employee.profile?.firstName || ''} ${employee.profile?.lastName || ''}`.trim(),
            value: employee.id,
          }))
        );
      }
    };

    const fetchSubcontractors = async () => {
      const response = await userManagementApi.getEmployees({
        role: "00004",
      });
      if (response.data?.data?.responses) {
        setSubcontractors(
          response.data.data.responses.map((subcontractor: any) => ({
            label: `${subcontractor.profile?.firstName || ''} ${subcontractor.profile?.lastName || ''}`.trim(),
            value: subcontractor.id,
          }))
        );
      }
    };
    fetchEmployees();
    fetchSubcontractors();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      const subcontractorIds = new Set(subcontractors.map((s) => s.value));
      const employeesMinusSubcontractors = employees.filter(
        (emp) => !subcontractorIds.has(emp.value)
      );
      setAssignedTo(employeesMinusSubcontractors);
    } else {
      setAssignedTo([]);
    }
  }, [employees, subcontractors]);

  useEffect(() => {
    if (!isOpen || !bodyRef.current) return;

    const checkAndScrollDropdown = () => {
      if (!bodyRef.current) return;

      const dropdowns = bodyRef.current.querySelectorAll(
        '[class*="dropdown-list"]'
      ) as NodeListOf<HTMLElement>;

      if (!dropdowns || dropdowns.length === 0) return;

      dropdowns.forEach((dropdown) => {
        const dropdownRect = dropdown.getBoundingClientRect();
        const bodyRect = bodyRef.current?.getBoundingClientRect();

        if (!bodyRect || !bodyRef.current) return;

        const dropdownBottom = dropdownRect.bottom;
        const bodyBottom = bodyRect.bottom;
        const dropdownTop = dropdownRect.top;
        const bodyTop = bodyRect.top;
        const padding = 40;

        const currentScrollTop = bodyRef.current.scrollTop;

        if (dropdownBottom > bodyBottom) {
          const overflowAmount = dropdownBottom - bodyBottom;
          const scrollAmount = overflowAmount + padding;
          const newScrollTop = currentScrollTop + scrollAmount;

          bodyRef.current.scrollTo({
            top: newScrollTop,
            behavior: "smooth",
          });

          setTimeout(() => {
            const recheckRect = dropdown.getBoundingClientRect();
            const recheckBodyRect = bodyRef.current?.getBoundingClientRect();
            if (
              recheckBodyRect &&
              recheckRect.bottom > recheckBodyRect.bottom
            ) {
              const additionalScroll =
                recheckRect.bottom - recheckBodyRect.bottom + padding;
              bodyRef.current?.scrollBy({
                top: additionalScroll,
                behavior: "smooth",
              });
            }
          }, 300);
        } else if (dropdownTop < bodyTop) {
          const overflowAmount = bodyTop - dropdownTop;
          const scrollAmount = overflowAmount + padding;
          const newScrollTop = currentScrollTop - scrollAmount;

          bodyRef.current.scrollTo({
            top: Math.max(0, newScrollTop),
            behavior: "smooth",
          });

          setTimeout(() => {
            const recheckRect = dropdown.getBoundingClientRect();
            const recheckBodyRect = bodyRef.current?.getBoundingClientRect();
            if (recheckBodyRect && recheckRect.top < recheckBodyRect.top) {
              const additionalScroll =
                recheckBodyRect.top - recheckRect.top + padding;
              bodyRef.current?.scrollBy({
                top: -additionalScroll,
                behavior: "smooth",
              });
            }
          }, 300);
        }
      });
    };

    const handleBodyClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[class*="select-container"]')) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              checkAndScrollDropdown();
            });
          });
        }, 50);
      }
    };

    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              if (
                element.classList &&
                element.classList.toString().includes("dropdown-list")
              ) {
                shouldCheck = true;
              }
            }
          });
        }
      });

      if (shouldCheck) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            checkAndScrollDropdown();
          });
        });
      }
    });

    const bodyElement = bodyRef.current;
    if (bodyElement) {
      bodyElement.addEventListener("click", handleBodyClick);
      observer.observe(bodyElement, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (bodyElement) {
        bodyElement.removeEventListener("click", handleBodyClick);
      }
      observer.disconnect();
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.taskType.trim()) {
      newErrors.taskType = "Task type is required";
    }

    if (!formData.taskName.trim()) {
      newErrors.taskName = "Task name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.startDate.trim()) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.startTime.trim()) {
      newErrors.startTime = "Start time is required";
    }

    if (!formData.dueDate.trim()) {
      newErrors.dueDate = "Due date is required";
    }

    if (!formData.dueTime.trim()) {
      newErrors.dueTime = "Due time is required";
    }

    if (!formData.priority.trim()) {
      newErrors.priority = "Priority is required";
    }

    if (!formData.assignedTo.trim()) {
      newErrors.assignedTo = "Assigned to is required";
    }

    if (!formData.subcontractor.trim()) {
      newErrors.subcontractor = "Subcontractor is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSave = async () => {
    if (!validateForm()) {
      notify("error", "Please fix all validation errors before saving");
      return;
    }

    const payload = {
      title: formData.taskName,
      assigneeId: formData.assignedTo
        ? parseInt(formData.assignedTo, 10)
        : undefined,
      startDate:
        formData.startDate && formData.startTime
          ? dayjs(formData.startDate)
            .hour(dayjs(formData.startTime).hour())
            .minute(dayjs(formData.startTime).minute())
            .second(0)
            .millisecond(0)
            .toISOString()
          : formData.startDate
            ? dayjs(formData.startDate).toISOString()
            : undefined,
      dueDate:
        formData.dueDate && formData.dueTime
          ? dayjs(formData.dueDate)
            .hour(dayjs(formData.dueTime).hour())
            .minute(dayjs(formData.dueTime).minute())
            .second(0)
            .millisecond(0)
            .toISOString()
          : formData.dueDate
            ? dayjs(formData.dueDate).toISOString()
            : undefined,
      priority: formData.priority,
      type: formData.taskType,
      ...(mode === "edit" && {
        status: formData?.status,
      }),
      subContractorId: formData.subcontractor
        ? parseInt(formData.subcontractor, 10)
        : undefined,
      description: formData.description,
      allDayChecked: formData.allDayChecked,
      ...(mode === "add" && {
        jobId: jobId
          ? typeof jobId === "string"
            ? parseInt(jobId, 10)
            : jobId
          : undefined,
      }),
    };

    try {
      await onSave(payload, mode);

      if (mode === "add") {
        setFormData({
          taskType: "",
          taskName: "",
          description: "",
          allDayChecked: false,
          startDate: "",
          startTime: "",
          dueDate: "",
          dueTime: "",
          priority: "",
          assignedTo: "",
          status: "",
          subcontractor: "",
        });
      }

    } catch (error) {
      console.error("Error saving task:", error);
    }

  };

  if (!isOpen) return null;
  return (
    <BaseModal open={isOpen} onClose={onClose} width="90%" maxWidth="600px">
      <div className={cx("add-new-task-container")}>
        <div className={cx("header")}>
          <h3 className={cx("title")}>
            {mode === "add" ? "Add New Task" : "Edit Task"}
          </h3>
          {mode === "add" && (
            <p className={cx("subtitle")}>Create a new task</p>
          )}
        </div>
        <div className={cx("body")} ref={bodyRef}>
          <div className={cx("row", "mb-[12px]")}>
            <div className={cx("item")}>
              <label className={cx("label")}>Task Type</label>
              <PrimarySelectInput
                name="taskType"
                placeholder="Select Task Type"
                value={formData.taskType}
                onChange={(value) => handleFieldChange("taskType", value)}
                options={TASK_TYPE_OPTIONS.map((option) => ({
                  label: option.label,
                  value: option.label,
                }))}
                className={cx("select-input", { error: !!errors.taskType })}
                classNameIcon={cx("select-input-icon")}
                errorMessage={errors.taskType}
              />
            </div>
          </div>

          <div className={cx("row", "mb-[19px]")}>
            <div className={cx("item")}>
              <label className={cx("label")}>Task Name</label>
              <PrimaryInput
                name="taskName"
                placeholder="Enter Task Name"
                value={formData.taskName}
                onChange={(e) => handleFieldChange("taskName", e.target.value)}
                errorMessage={errors.taskName}
                className={cx("input", { error: !!errors.taskName })}
              />
            </div>
          </div>

          <div className={cx("row", "mb-[22px]")}>
            <div className={cx("item")}>
              <label className={cx("label")}>Description</label>
              <textarea
                className={cx("textarea", "h-[72px]", {
                  error: !!errors.description,
                })}
                placeholder="Enter Description"
                value={formData.description}
                onChange={(e) =>
                  handleFieldChange("description", e.target.value)
                }
                rows={3}
              />
              {errors.description && (
                <div className={cx("error-message")}>{errors.description}</div>
              )}
            </div>
          </div>

          <div className={cx("row", "mb-[22px]")}>
            <div className={cx("all-day-checkbox")}>
              <input
                type="checkbox"
                checked={formData.allDayChecked}
                onChange={(e) =>
                  handleFieldChange("allDayChecked", String(e.target.checked))
                }
                className={cx("checkbox")}
              />
              <label className={cx("checkbox-label")}>All Day</label>
            </div>
          </div>

          <div className={cx("row", "mb-[20px]")}>
            <div className={cx("item")}>
              <label className={cx("label")}>Start Date</label>
              <div className={cx("date-time-input")}>
                <PrimaryInput
                  name="startDate"
                  placeholder="Enter Start Date"
                  value={formData.startDate}
                  onChange={(e) =>
                    handleFieldChange("startDate", e.target.value)
                  }
                  className={cx("input", { error: !!errors.startDate })}
                  type="date"
                  errorMessage={errors.startDate}
                  showCalendarIcon={false}
                />
                <PrimaryInput
                  name="startTime"
                  placeholder="Enter Start Time"
                  value={formData.startTime}
                  onChange={(e) =>
                    handleFieldChange("startTime", e.target.value)
                  }
                  className={cx("input", { error: !!errors.startTime })}
                  type="time"
                  errorMessage={errors.startTime}
                  showCalendarIcon={false}
                />
              </div>
            </div>

            <div className={cx("item")}>
              <label className={cx("label")}>Due Date</label>
              <div className={cx("date-time-input")}>
                <PrimaryInput
                  name="dueDate"
                  placeholder="Enter Due Date"
                  value={formData.dueDate}
                  onChange={(e) => handleFieldChange("dueDate", e.target.value)}
                  className={cx("input", { error: !!errors.dueDate })}
                  type="date"
                  errorMessage={errors.dueDate}
                  showCalendarIcon={false}
                />
                <PrimaryInput
                  name="dueTime"
                  placeholder="Enter Due Time"
                  value={formData.dueTime}
                  onChange={(e) => handleFieldChange("dueTime", e.target.value)}
                  className={cx("input", { error: !!errors.dueTime })}
                  type="time"
                  errorMessage={errors.dueTime}
                  showCalendarIcon={false}
                />
              </div>
            </div>
          </div>

          <div className={cx("row", "mb-[20px]")}>
            <div className={cx("item")}>
              <label className={cx("label")}>Priority</label>
              <PrimarySelectInput
                name="priority"
                placeholder="Select"
                value={formData.priority}
                onChange={(value) => handleFieldChange("priority", value)}
                options={PRIORITY_OPTIONS.map((option) => ({
                  label: option.label,
                  value: option.label,
                }))}
                className={cx("select-input", { error: !!errors.priority })}
                classNameIcon={cx("select-input-icon")}
                errorMessage={errors.priority}
              />
            </div>

            <div className={cx("item")}>
              <label className={cx("label")}>Assigned To</label>
              <PrimarySelectInput
                name="assignedTo"
                placeholder="Select"
                value={formData.assignedTo}
                onChange={(value) => handleFieldChange("assignedTo", value)}
                options={assignedTo}
                className={cx("select-input", { error: !!errors.assignedTo })}
                classNameIcon={cx("select-input-icon")}
                errorMessage={errors.assignedTo}
              />
            </div>
          </div>

          <div className={cx("row")}>
            <div className={cx("item")}>
              <label className={cx("label")}>Sub Contractor</label>
              <PrimarySelectInput
                name="subcontractor"
                placeholder="Select"
                value={formData.subcontractor}
                onChange={(value) => handleFieldChange("subcontractor", value)}
                options={subcontractors}
                className={cx("select-input", {
                  error: !!errors.subcontractor,
                })}
                classNameIcon={cx("select-input-icon")}
                errorMessage={errors.subcontractor}
              />
            </div>

            {mode === "add" ?
              <div className={cx("item")}>
              </div>
              :
              <div className={cx("item")}>
                <label className={cx("label")}>Status</label>
                <PrimarySelectInput
                  name="status"
                  placeholder="Select"
                  value={formData.status}
                  onChange={(value) => {
                    console.log("status selected:", value);
                    handleFieldChange("status", value);
                  }}
                  options={STATUS_OPTIONS.map((option) => ({
                    label: option.label,
                    value: option.value,
                  }))}
                  className={cx("select-input", { error: !!errors.status })}
                  classNameIcon={cx("select-input-icon")}
                  errorMessage={errors.status}
                />
              </div>
            }
          </div>
        </div>

        <div className={cx("footer")}>
          <PrimaryButton
            title="Cancel"
            onClick={handleCancel}
            className={cx("cancel-button")}
            disabled={isLoading}
          />

          <PrimaryButton
            title={mode === "add" ? "Create Task" : "Save Task"}
            onClick={handleSave}
            className={cx("save-button")}
            loading={isLoading}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default AddNewTaskModal;
